// Informational only. Subscription options, selling plans and checkout remain
// owned by the existing subscription form.
const FALLBACK = 'Subscribe by the end of this month to be eligible for next month’s box.';
const POLL_MS = 15_000;
const FRESH_MS = 30_000;
const london = new Intl.DateTimeFormat('en-GB', {timeZone: 'Europe/London', year: 'numeric', month: '2-digit'});
const deadlineFormat = new Intl.DateTimeFormat('en-GB', {timeZone: 'Europe/London', dateStyle: 'long', timeStyle: 'short'});

function monthAt(time) {
  const parts = london.formatToParts(new Date(time));
  return `${parts.find(part => part.type === 'year').value}-${parts.find(part => part.type === 'month').value}`;
}

function upcomingMonthAt(time) {
  const [year, month] = monthAt(time).split('-').map(Number);
  return `${year + (month === 12 ? 1 : 0)}-${String(month % 12 + 1).padStart(2, '0')}`;
}

export function cutoffState(snapshot, now) {
  const deadline = Date.parse(snapshot?.cutoffAt);
  if (!snapshot || snapshot.available !== true || !/^\d{4}-\d{2}$/.test(snapshot.boxMonth) ||
      typeof snapshot.revision !== 'string' || !snapshot.revision ||
      !Number.isFinite(Date.parse(snapshot.serverNow)) || !Number.isFinite(deadline) ||
      !Number.isFinite(now) || deadline <= now || upcomingMonthAt(now) !== snapshot.boxMonth ||
      monthAt(deadline) !== snapshot.boxMonth) return {active: false};
  return {active: true, seconds: Math.ceil((deadline - now) / 1000), deadline};
}

export function formatDuration(seconds) {
  let remaining = Math.max(0, Math.floor(seconds));
  const days = Math.floor(remaining / 86400);
  remaining %= 86400;
  const hours = Math.floor(remaining / 3600);
  remaining %= 3600;
  const minutes = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const pad = value => String(value).padStart(2, '0');
  return `${days ? `${days}d ` : ''}${days || hours ? `${pad(hours)}h ` : ''}${days || hours || minutes ? `${pad(minutes)}m ` : ''}${pad(secs)}s`;
}

export function mountCutoff(root) {
  if (root.dataset.cutoffMounted) return;
  root.dataset.cutoffMounted = 'true';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let snapshot = null;
  let anchor = null;
  let request = null;
  let poll = null;
  let tick = null;
  let digits = [];
  let timer = null;
  let previous = '';
  let accessible = null;
  let suspended = false;
  const animations = new Set();
  const element = (className, text = '') => {
    const node = document.createElement('span');
    node.className = className;
    node.textContent = text;
    return node;
  };
  const clearAnimations = () => {
    for (const timeout of animations) clearTimeout(timeout);
    animations.clear();
  };
  const fallback = () => {
    clearAnimations();
    if (root.textContent !== FALLBACK) root.textContent = FALLBACK;
    timer = null;
    previous = '';
    digits = [];
    accessible = null;
  };
  const render = state => {
    const value = formatDuration(state.seconds);
    if (!timer) {
      const visual = element('library-cutoff__visual');
      visual.setAttribute('aria-hidden', 'true');
      timer = element('landing-countdown');
      accessible = element('library-cutoff__accessible');
      visual.append(element('library-cutoff__prefix', 'Subscribe within'), timer,
        element('library-cutoff__suffix', 'for next month’s box'));
      root.replaceChildren(visual, accessible);
    }
    const description = `Subscribe by ${deadlineFormat.format(new Date(state.deadline))} London time for next month’s box.`;
    if (accessible.textContent !== description) accessible.textContent = description;
    if (previous.replace(/\d/g, '#') !== value.replace(/\d/g, '#')) {
      clearAnimations();
      digits = [];
      timer.replaceChildren();
      for (const character of value) {
        const node = element(/\d/.test(character) ? 'landing-countdown__digit' : character === ' ' ? 'landing-countdown__space' : 'landing-countdown__static', /\d/.test(character) ? '' : character);
        if (/\d/.test(character)) {
          node.dataset.value = character;
          node.append(element('landing-countdown__digit-current', character), element('landing-countdown__digit-next', character));
          digits.push(node);
        }
        timer.append(node);
      }
    } else {
      const values = value.match(/\d/g) || [];
      digits.forEach((node, index) => {
        const nextValue = values[index];
        if (node.dataset.value === nextValue) return;
        const current = node.firstElementChild;
        const next = node.lastElementChild;
        node.dataset.value = nextValue;
        if (reduced.matches || !previous) {
          current.textContent = next.textContent = nextValue;
          node.classList.remove('is-stepping');
          return;
        }
        next.textContent = nextValue;
        node.classList.remove('is-stepping');
        void node.offsetWidth;
        node.classList.add('is-stepping');
        const timeout = setTimeout(() => {
          current.textContent = next.textContent = nextValue;
          node.classList.remove('is-stepping');
          animations.delete(timeout);
        }, 260);
        animations.add(timeout);
      });
    }
    previous = value;
  };
  const update = () => {
    if (!anchor || performance.now() - anchor.received > FRESH_MS || suspended) return fallback();
    const state = cutoffState(snapshot, anchor.server + performance.now() - anchor.received);
    if (state.active) render(state);
    else fallback();
  };
  const refresh = async () => {
    if (suspended || document.hidden || request) return;
    request = new AbortController();
    const controller = request;
    const started = performance.now();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const url = new URL(root.dataset.cutoffEndpoint, location.origin);
      url.searchParams.set('_fresh', `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const response = await fetch(url, {cache: 'no-store', credentials: 'same-origin', signal: controller.signal, headers: {Accept: 'application/json'}});
      if (!response.ok || Number(response.headers.get('Age') || 0) > 0) throw new Error('Unavailable');
      const value = await response.json();
      const received = performance.now();
      const server = Date.parse(value.serverNow);
      if (!Number.isFinite(server) || (anchor && server < anchor.server + started - anchor.received - 3000)) throw new Error('Stale clock');
      if (suspended || controller.signal.aborted) return;
      snapshot = value;
      anchor = {server: server + (received - started) / 2, received};
      update();
    } catch {
      snapshot = anchor = null;
      fallback();
    } finally {
      clearTimeout(timeout);
      if (request === controller) request = null;
    }
  };
  const stop = () => {
    suspended = true;
    clearInterval(poll);
    clearInterval(tick);
    request?.abort();
    fallback();
  };
  const start = () => {
    clearInterval(poll);
    clearInterval(tick);
    suspended = false;
    anchor = snapshot = null;
    fallback();
    void refresh();
    poll = setInterval(refresh, POLL_MS);
    tick = setInterval(update, 1000);
  };
  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', start);
  document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
  window.addEventListener('focus', () => { if (!document.hidden) void refresh(); });
  reduced.addEventListener('change', () => { clearAnimations(); previous = ''; timer = null; update(); });
  start();
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-library-cutoff]').forEach(mountCutoff);
}
